import React, { useState } from "react";
import styles from "./MobileCustomerTopHeader.module.scss";
import { useTenant } from "../../../tenant/branding/useTenant";
import { MobileCustomerTopHeaderProps } from "./CustomerMenu.types";
import { BellNotificationIcon, MobileMenuIcon } from "../../../assets/customermenu-icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../stores/store";
import { ApplicationRoutes } from "../../../routes";
import { notificationApi, useLazyGetUnreadNotificationsCountQuery } from "../../../service/notification-service";
import { setUnreadCount } from "../../../stores/slices/notification-slice";
import MobileCustomerMenu from "./MobileCustomerMenu";
import { useTheme } from "../../../theme/useTheme";
import LoadedImage from "../../../common/components/loaded-image/LoadedImage";

const SunIcon: React.FC = () => (
	<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
		<circle cx="12" cy="12" r="4.2" />
		<path d="M12 2.5v2.2M12 19.3v2.2M4.6 4.6l1.55 1.55M17.85 17.85l1.55 1.55M2.5 12h2.2M19.3 12h2.2M4.6 19.4l1.55-1.55M17.85 6.15l1.55-1.55" />
	</svg>
);

const MoonIcon: React.FC = () => (
	<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
		<path d="M20.5 14.5a8.5 8.5 0 1 1-11-11 0.6 0.6 0 0 1 0.79 0.79 6.6 6.6 0 0 0 9.42 9.42 0.6 0.6 0 0 1 0.79 0.79z" />
	</svg>
);

const MobileCustomerTopHeader: React.FC<MobileCustomerTopHeaderProps> = ({
	activeMenu = "dashboard",
	activeSubMenu,
	onMenuClick,
	onSubMenuClick,
	userName = "Customer",
	userAvatar = "",
	onLogOutClick,

}) => {
	const navigate = useNavigate();
	const dispatch = useDispatch<AppDispatch>();
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const { mode, setPreference } = useTheme();
	const { logo, name } = useTenant();
	const unreadCount = useSelector((state: RootState) => state.notifications.unreadCount);
	const [fetchUnreadCount] = useLazyGetUnreadNotificationsCountQuery();

	const handleNotificationsClick = async () => {
		dispatch(notificationApi.util.invalidateTags(["Notifications"]));
		const result = await fetchUnreadCount();
		if (result.data !== undefined) dispatch(setUnreadCount(result.data));
		navigate(`/${ApplicationRoutes.CUSTOMER_NOTIFICATIONS}`);
	};

	const toggleMobileMenu = () => {
		setIsMobileMenuOpen(!isMobileMenuOpen);
	};

	const toggleTheme = () => {
		setPreference(mode === "dark" ? "light" : "dark");
	};

	const closeMobileMenu = () => {
		setIsMobileMenuOpen(false);
	};


	return (
		<>
			<div className={styles.menuContainer}>
				{/* Logo */}
				<div className={styles.logoIcon}>
					<LoadedImage src={logo} alt={name} onClick={() => {
						navigate('/dashboard');

					}} />
				</div>


				{/* Right: notification pill + avatar */}
				<div className={styles.headerRight}>
					<button
						className={styles.themeBtn}
						onClick={toggleTheme}
						title={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
						aria-label="Toggle theme"
					>
						{mode === "dark" ? <SunIcon /> : <MoonIcon />}
					</button>
					<button className={styles.notificationPill} onClick={handleNotificationsClick} title="Notifications">
						{unreadCount > 0 && (
							<span className={styles.notificationCount}>
								{unreadCount > 99 ? "99+" : unreadCount}
							</span>
						)}
						<BellNotificationIcon />
					</button>

					<button className={styles.menuBtn} onClick={toggleMobileMenu} aria-label="Menu">
						<MobileMenuIcon />
					</button>
				</div>
			</div>

			{/* Mobile Menu */}
			<MobileCustomerMenu
				isOpen={isMobileMenuOpen}
				onClose={closeMobileMenu}
				activeMenu={activeMenu}
				activeSubMenu={activeSubMenu}
				onMenuClick={menuId => {
					onMenuClick(menuId);
					// Don't close menu immediately, let user navigate submenus
				}}
				onSubMenuClick={subMenuId => {
					onSubMenuClick(subMenuId);
					closeMobileMenu(); // Close menu after submenu selection
				}}
				onLogOutClick={onLogOutClick}
				userName={`${userName}`}
				userAvatar={userAvatar}
			/>
		</>
	);
};

export default MobileCustomerTopHeader;
